import { isDefined } from "@clipboard-health/util-ts";
import constate from "constate";
import { type ReactNode, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

export const EMBEDDED_MODE_KEY = "hiring-embedded-mode";
const EMBEDDED_PLACEMENT_ID_KEY = "hiring-embedded-placement-id";
const DEFAULT_PAGE_MAX_WIDTH = "520px";
const EMBEDDED_PAGE_MAX_WIDTH = "1024px";

function useEmbeddedModeState() {
  // Check embedded mode only once on initialization using window.location
  // This doesn't require router context and is more reliable for initial detection
  const [isEmbedded, setIsEmbedded] = useState(() => {
    // Check window.location for initial page load
    const searchParams = new URLSearchParams(globalThis.location.search);
    const urlEmbedded = searchParams.get("embedded") === "true";

    // Always capture placementId if present in URL (needs to be stored before any redirects happen)
    // This must happen regardless of whether embedded mode is already persisted
    const placementId = searchParams.get("placementId");
    if (isDefined(placementId)) {
      sessionStorage.setItem(EMBEDDED_PLACEMENT_ID_KEY, placementId);
    }

    // Check sessionStorage for persistence across navigation
    const persisted = sessionStorage.getItem(EMBEDDED_MODE_KEY) === "true";
    if (persisted) {
      return true;
    }

    // Store in sessionStorage if detected in URL
    if (urlEmbedded) {
      sessionStorage.setItem(EMBEDDED_MODE_KEY, "true");
    }

    return urlEmbedded;
  });

  // When embedded, set a CSS variable with the actual viewport height
  // This is needed because 100vh in an iframe refers to the browser viewport, not the iframe
  useEffect(() => {
    if (!isEmbedded) {
      return undefined;
    }

    function setViewportHeight(height?: number) {
      // Use provided height from parent, or fall back to visualViewport/innerHeight
      const visualHeight = window.visualViewport?.height;
      const { innerHeight } = globalThis;
      const vh = height ?? visualHeight ?? innerHeight;

      document.documentElement.style.setProperty("--embedded-viewport-height", `${vh}px`);
    }

    // Listen for parent height message from the embedding page
    function handleParentMessage(event: MessageEvent) {
      const data = event.data as { type?: string; height?: number } | undefined;
      if (data?.type === "parentHeight" && typeof data.height === "number") {
        setViewportHeight(data.height);
      }
    }

    function handleResize() {
      setViewportHeight();
    }

    setViewportHeight();
    // Also set after a small delay to catch any async layout changes
    setTimeout(() => {
      setViewportHeight();
    }, 100);

    // Listen for messages from parent window
    window.addEventListener("message", handleParentMessage);
    // Also listen to resize events as fallback
    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("message", handleParentMessage);
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
    };
  }, [isEmbedded]);

  return { isEmbedded, setIsEmbedded };
}

const [EmbeddedModeContextProvider, useEmbeddedMode] = constate(useEmbeddedModeState);

function EmbeddedModeStyles() {
  const { isEmbedded } = useEmbeddedMode();
  return (
    <style>
      {`:root { --page-max-width: ${
        isEmbedded ? EMBEDDED_PAGE_MAX_WIDTH : DEFAULT_PAGE_MAX_WIDTH
      }; }`}
    </style>
  );
}

interface EmbeddedModeProviderProps {
  children: ReactNode;
}

function EmbeddedModeProvider({ children }: EmbeddedModeProviderProps) {
  return (
    <EmbeddedModeContextProvider>
      <EmbeddedModeStyles />
      {children}
    </EmbeddedModeContextProvider>
  );
}

function EmbeddedRouteSync() {
  const { isEmbedded } = useEmbeddedMode();
  const history = useHistory();

  useEffect(() => {
    if (!isEmbedded) {
      return undefined;
    }

    const unlisten = history.listen((location) => {
      window.parent.postMessage(
        { type: "routeChange", path: location.pathname, search: location.search },
        "*"
      );
    });

    return unlisten;
  }, [isEmbedded, history]);

  return null;
}

// Handles embedded mode redirects using React Router.
// Must be placed inside a Router context.
function EmbeddedPlacementRedirect() {
  const { isEmbedded } = useEmbeddedMode();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    if (!isEmbedded) {
      return;
    }

    // Get placementId from sessionStorage (stored during initial embedded mode detection)
    const placementId = sessionStorage.getItem(EMBEDDED_PLACEMENT_ID_KEY);

    if (isDefined(placementId)) {
      // Clear the stored placementId so we don't redirect again on subsequent navigations
      sessionStorage.removeItem(EMBEDDED_PLACEMENT_ID_KEY);

      // Only redirect if we have a placementId and we're not already on a placements page
      if (!location.pathname.includes("/placements/")) {
        history.replace(`/home-v2/placements/${placementId}?embedded=true`);
      }
    }
  }, [isEmbedded, history, location.pathname]);

  return null;
}

export { EmbeddedModeProvider, EmbeddedPlacementRedirect, EmbeddedRouteSync, useEmbeddedMode };
